import { updateConfigSettings } from '@api/portalServices';
import type { Setting } from '@api/types';
import type { ApiError } from '@errorHandler/errors';
import {
  AccountRecoveryEnabledValue,
  AuthnLevel,
  DiscardEnrollmentPhotosValue,
  FieldNames,
  type FormValues,
  InstanceKey,
  updateAccountRecoveryEnabledSettingsTemplate,
  updateDiscardEnrollmentPhotosSettingsTemplate,
  updateSettingsTemplate,
  updateUserCanShowLiveFeedSettingsTemplate,
  UserCanShowLiveFeedValue,
} from '@portal/containers/security/constants';
import { useMutation } from '@tanstack/react-query';
import { sleep } from '@utils/utils';

export type UpdateConfigParams = {
  ussSetting: Setting | undefined;
  iamSetting: Setting | undefined;
  userCanShowLiveFeedSetting: Setting | undefined;
  discardEnrollmentPhotosSetting: Setting | undefined;
  accountRecoveryEnabledSetting: Setting | undefined;
  formValues: FormValues;
};

export function computeNewSettingValues(formValues: FormValues): {
  ussNewSettingValue: AuthnLevel;
  iamNewSettingValue: AuthnLevel;
} {
  return {
    ussNewSettingValue: formValues[FieldNames.USS_ONE_FACTOR_VALUE]
      ? AuthnLevel.ICP_MFA_IF_ENROLLED
      : formValues[FieldNames.USS_VALUE],
    iamNewSettingValue: formValues[FieldNames.IAM_ONE_FACTOR_VALUE]
      ? AuthnLevel.ICP_MFA_IF_ENROLLED
      : formValues[FieldNames.IAM_VALUE],
  };
}

export function buildUpdatedSettings({
  ussSetting,
  iamSetting,
  userCanShowLiveFeedSetting,
  discardEnrollmentPhotosSetting,
  accountRecoveryEnabledSetting,
  formValues,
}: UpdateConfigParams): Setting[] {
  const settings: Setting[] = [];
  const { ussNewSettingValue, iamNewSettingValue } = computeNewSettingValues(formValues);
  if (ussSetting?.value !== ussNewSettingValue) {
    settings.push({
      ...updateSettingsTemplate,
      settingId: ussSetting?.settingId,
      instanceKey: InstanceKey.USS,
      value: ussNewSettingValue,
    });
  }

  if (iamSetting?.value !== iamNewSettingValue) {
    settings.push({
      ...updateSettingsTemplate,
      settingId: iamSetting?.settingId,
      instanceKey: InstanceKey.IAM,
      value: iamNewSettingValue,
    });
  }

  const userCanShowLiveFeedNewSettingValue = formValues[FieldNames.USER_CAN_SHOW_LIVE_FEED_VALUE]
    ? UserCanShowLiveFeedValue.ON
    : UserCanShowLiveFeedValue.OFF;
  // Diff-only update: the setting is only written when the value has changed from what
  // the backend returned. For tenants without the face-recognition entitlement,
  // userCanShowLiveFeedSetting is undefined and the form default is ON, so
  // shouldUpdateUserCanShowLiveFeedSetting will always be false — the field is
  // never submitted to the backend even though it remains in the form state.
  const shouldUpdateUserCanShowLiveFeedSetting = userCanShowLiveFeedSetting
    ? userCanShowLiveFeedSetting.value.toLowerCase() !== userCanShowLiveFeedNewSettingValue
    : userCanShowLiveFeedNewSettingValue !== UserCanShowLiveFeedValue.ON;

  if (shouldUpdateUserCanShowLiveFeedSetting) {
    settings.push({
      ...updateUserCanShowLiveFeedSettingsTemplate,
      instanceKey:
        userCanShowLiveFeedSetting?.instanceKey ??
        updateUserCanShowLiveFeedSettingsTemplate.instanceKey,
      settingId: userCanShowLiveFeedSetting?.settingId,
      value: userCanShowLiveFeedNewSettingValue,
    });
  }

  const discardEnrollmentPhotosNewSettingValue = formValues[
    FieldNames.DISCARD_ENROLLMENT_PHOTOS_VALUE
  ]
    ? DiscardEnrollmentPhotosValue.ON
    : DiscardEnrollmentPhotosValue.OFF;
  const storedDiscardValue = discardEnrollmentPhotosSetting?.value.toLowerCase();
  const storedDiscardIsOn =
    storedDiscardValue === DiscardEnrollmentPhotosValue.ON || storedDiscardValue === 'true';
  const shouldUpdateDiscardEnrollmentPhotosSetting = discardEnrollmentPhotosSetting
    ? storedDiscardIsOn !== (discardEnrollmentPhotosNewSettingValue === DiscardEnrollmentPhotosValue.ON)
    : discardEnrollmentPhotosNewSettingValue !== DiscardEnrollmentPhotosValue.ON;

  if (shouldUpdateDiscardEnrollmentPhotosSetting) {
    settings.push({
      ...updateDiscardEnrollmentPhotosSettingsTemplate,
      instanceKey:
        discardEnrollmentPhotosSetting?.instanceKey ??
        updateDiscardEnrollmentPhotosSettingsTemplate.instanceKey,
      settingId: discardEnrollmentPhotosSetting?.settingId,
      value: discardEnrollmentPhotosNewSettingValue,
    });
  }

  const accountRecoveryEnabledNewValue = formValues[FieldNames.ACCOUNT_RECOVERY_ENABLED_VALUE]
    ? AccountRecoveryEnabledValue.ON
    : AccountRecoveryEnabledValue.OFF;
  const shouldUpdateAccountRecoveryEnabled = accountRecoveryEnabledSetting
    ? accountRecoveryEnabledSetting.value.toLowerCase() !== accountRecoveryEnabledNewValue
    : accountRecoveryEnabledNewValue !== AccountRecoveryEnabledValue.ON;

  if (shouldUpdateAccountRecoveryEnabled) {
    // account-recovery-enabled is written without an instanceKey.
    settings.push({
      ...updateAccountRecoveryEnabledSettingsTemplate,
      settingId: accountRecoveryEnabledSetting?.settingId,
      value: accountRecoveryEnabledNewValue,
    });
  }

  return settings;
}

export const useUpdateConfigSettings = () => {
  return useMutation<void, ApiError, UpdateConfigParams>({
    mutationFn: async params => {
      const settings = buildUpdatedSettings(params);
      if (settings.length > 0) {
        await updateConfigSettings({ settings });
        await sleep(500);
      }
    },
    retry: false,
  });
};
